import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, MessageSquare, Eye, Bot, User, FileSearch, Calendar as CalendarIcon, Building2, Brain, FlaskConical, Sparkles, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';
import { useCohortFilterStore, useOrgFilterStore } from '@/hooks/useOrgFilterStore';
import {
  useConversationsAudit,
  useConversationMessages,
  useAuditFilterOptions,
  type ConversationRow,
  type ConversationTool,
} from '@/hooks/useAIConversationsAudit';

const TOOL_OPTIONS: { value: ConversationTool; label: string; icon: React.ReactNode }[] = [
  { value: 'all', label: 'Todas las herramientas', icon: <Bot className="w-4 h-4" /> },
  { value: 'lia', label: 'LIA', icon: <Brain className="w-4 h-4" /> },
  { value: 'challenge', label: 'Lab. Retos y Soluciones', icon: <FlaskConical className="w-4 h-4" /> },
  { value: 'prototype', label: 'Agente de Prototipado', icon: <Sparkles className="w-4 h-4" /> },
];

function toolBadge(contextType: string | null) {
  switch (contextType) {
    case 'lia':
      return <Badge variant="secondary" className="gap-1"><Brain className="w-3 h-3" />LIA</Badge>;
    case 'challenge':
      return <Badge variant="secondary" className="gap-1 bg-amber-500/10 text-amber-600"><FlaskConical className="w-3 h-3" />Lab. Retos</Badge>;
    case 'prototype':
      return <Badge variant="secondary" className="gap-1 bg-purple-500/10 text-purple-600"><Sparkles className="w-3 h-3" />Prototipado</Badge>;
    default:
      return <Badge variant="outline">{contextType || 'Otro'}</Badge>;
  }
}

export default function AIConversationsAuditPage() {
  const { currentRole } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';

  const [search, setSearch] = useState('');
  const [toolType, setToolType] = useState<ConversationTool>('all');
  const [organizationId, setOrganizationId] = useOrgFilterStore();
  const [cohortId, setCohortId] = useCohortFilterStore();
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedSession, setSelectedSession] = useState<ConversationRow | null>(null);

  const filters = useMemo(() => ({
    search,
    toolType,
    organizationId,
    cohortId,
    dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
    dateTo: dateTo ? new Date(`${dateTo}T23:59:59`).toISOString() : undefined,
  }), [search, toolType, organizationId, cohortId, dateFrom, dateTo]);

  const { data: conversations = [], isLoading } = useConversationsAudit(filters);
  const { organizations, cohorts } = useAuditFilterOptions();
  const { data: messages = [], isLoading: messagesLoading } = useConversationMessages(selectedSession?.session_id ?? null);

  const stats = useMemo(() => {
    const byTool = conversations.reduce((acc, c) => {
      acc[c.context_type || 'otro'] = (acc[c.context_type || 'otro'] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const totalMessages = conversations.reduce((s, c) => s + c.message_count, 0);
    return { total: conversations.length, byTool, totalMessages };
  }, [conversations]);

  const clearFilters = () => {
    setSearch('');
    setToolType('all');
    setOrganizationId('all');
    setCohortId('all');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      <div className="flex items-center gap-3">
        <FileSearch className="w-7 h-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Auditoría de Conversaciones IA</h1>
          <p className="text-muted-foreground text-sm">
            Conversaciones con LIA, Lab. de Retos y Soluciones, y Agente de Prototipado
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-primary">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Conversaciones</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-blue-500">{stats.totalMessages}</p>
            <p className="text-sm text-muted-foreground">Mensajes totales</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-amber-500">{stats.byTool.lia || 0}</p>
            <p className="text-sm text-muted-foreground">LIA</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-purple-500">
              {(stats.byTool.challenge || 0) + (stats.byTool.prototype || 0)}
            </p>
            <p className="text-sm text-muted-foreground">Lab + Prototipado</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="w-4 h-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="relative col-span-full md:col-span-2 lg:col-span-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por usuario, correo, título de conversación u organización"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={toolType} onValueChange={(v) => setToolType(v as ConversationTool)}>
              <SelectTrigger>
                <SelectValue placeholder="Herramienta" />
              </SelectTrigger>
              <SelectContent>
                {TOOL_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="flex items-center gap-2">{opt.icon}{opt.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {isSuperAdmin && (
              <Select value={organizationId} onValueChange={setOrganizationId}>
                <SelectTrigger>
                  <SelectValue placeholder="Organización" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las organizaciones</SelectItem>
                  {organizations.map((o: any) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={cohortId} onValueChange={setCohortId}>
              <SelectTrigger>
                <SelectValue placeholder="Cohorte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las cohortes</SelectItem>
                {cohorts.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-2 col-span-full md:col-span-2 lg:col-span-3">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground">Desde</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground">Hasta</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div className="flex items-end">
                <Button variant="ghost" onClick={clearFilters}>Limpiar</Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="w-4 h-4" />
            Conversaciones ({conversations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No se encontraron conversaciones con los filtros actuales</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Correo</TableHead>
                    <TableHead>Herramienta</TableHead>
                    <TableHead>Organización</TableHead>
                    <TableHead>Cohortes</TableHead>
                    <TableHead className="text-right">Mensajes</TableHead>
                    <TableHead>Última actividad</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {conversations.map(conv => (
                    <TableRow key={conv.session_id} className="hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          {conv.user_name}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{conv.user_email}</TableCell>
                      <TableCell>{toolBadge(conv.context_type)}</TableCell>
                      <TableCell>
                        {conv.organization_name ? (
                          <span className="flex items-center gap-1 text-sm">
                            <Building2 className="w-3 h-3 text-muted-foreground" />
                            {conv.organization_name}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[180px]">
                          {conv.cohort_names.slice(0, 2).map((c, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{c}</Badge>
                          ))}
                          {conv.cohort_names.length > 2 && (
                            <Badge variant="outline" className="text-xs">+{conv.cohort_names.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">{conv.message_count}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-3 h-3" />
                          {format(new Date(conv.updated_at), "d MMM yyyy HH:mm", { locale: es })}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedSession(conv)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de detalle */}
      <Dialog open={!!selectedSession} onOpenChange={(o) => !o && setSelectedSession(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              {selectedSession?.title || 'Conversación'}
            </DialogTitle>
            <DialogDescription>
              {selectedSession && (
                <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                  <span className="flex items-center gap-1"><User className="w-3 h-3" />{selectedSession.user_name}</span>
                  <span className="text-muted-foreground">{selectedSession.user_email}</span>
                  {toolBadge(selectedSession.context_type)}
                  {selectedSession.organization_name && (
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Building2 className="w-3 h-3" />{selectedSession.organization_name}
                    </span>
                  )}
                  <span className="text-muted-foreground">
                    {format(new Date(selectedSession.created_at), "d MMM yyyy HH:mm", { locale: es })}
                  </span>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 pr-4">
            {messagesLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">Sin mensajes</div>
            ) : (
              <div className="space-y-3">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`rounded-lg p-3 ${
                      msg.role === 'user'
                        ? 'bg-primary/10 ml-8'
                        : msg.role === 'assistant'
                        ? 'bg-muted mr-8'
                        : 'bg-amber-500/10 text-sm italic'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-medium uppercase text-muted-foreground">
                        {msg.role === 'user' ? 'Usuario' : msg.role === 'assistant' ? 'Asistente' : msg.role}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(msg.created_at), "d MMM HH:mm:ss", { locale: es })}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
